import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import defaultContent from './default-content.json';

const CmsContentContext = createContext(null);

const cloneContent = (value) => JSON.parse(JSON.stringify(value));

const getAtPath = (object, contentKey) => contentKey
  .split('.')
  .reduce((value, segment) => value?.[segment], object);

const setAtPath = (object, contentKey, nextValue) => {
  const segments = contentKey.split('.');
  const root = Array.isArray(object) ? [...object] : { ...object };
  let sourceCursor = object;
  let targetCursor = root;

  segments.forEach((segment, index) => {
    if (index === segments.length - 1) {
      targetCursor[segment] = nextValue;
      return;
    }

    const sourceChild = sourceCursor?.[segment];
    const targetChild = Array.isArray(sourceChild) ? [...sourceChild] : { ...sourceChild };
    targetCursor[segment] = targetChild;
    sourceCursor = sourceChild;
    targetCursor = targetChild;
  });

  return root;
};

export const CmsContentProvider = ({ mode = 'public', children }) => {
  const [published, setPublished] = useState(() => cloneContent(defaultContent));
  const [draft, setDraft] = useState(() => cloneContent(defaultContent));
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;

    fetch('/cms-api/content', { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`Content request failed: ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        if (!active || !payload?.content) return;
        setPublished(cloneContent(payload.content));
        setDraft(cloneContent(payload.content));
        document.title = payload.content.documentTitle || defaultContent.documentTitle;
      })
      .catch(() => {
        document.title = defaultContent.documentTitle;
      })
      .finally(() => {
        if (active) setLoaded(true);
      });

    return () => {
      active = false;
    };
  }, []);

  const content = mode === 'editor' ? draft : published;

  const valueFor = useCallback(
    (contentKey) => getAtPath(content, contentKey),
    [content],
  );

  const update = useCallback((contentKey, nextValue) => {
    if (mode !== 'editor') return;
    setDraft((current) => setAtPath(current, contentKey, nextValue));
    setDirty(true);
  }, [mode]);

  const cancel = useCallback(() => {
    setDraft(cloneContent(published));
    setDirty(false);
  }, [published]);

  const save = useCallback(async () => {
    if (mode !== 'editor' || saving) return;
    setSaving(true);
    try {
      const response = await fetch('/cms-api/revisions', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: draft }),
      });
      if (response.status === 401) {
        window.location.assign('/admin/login');
        return;
      }
      if (!response.ok) throw new Error(`Publish failed: ${response.status}`);
      const payload = await response.json();
      const savedContent = cloneContent(payload.content || draft);
      setPublished(savedContent);
      setDraft(cloneContent(savedContent));
      setDirty(false);
      document.title = savedContent.documentTitle || defaultContent.documentTitle;
    } catch {
      window.alert('Save failed. Your unsaved text is still on this page; please try Save again.');
    } finally {
      setSaving(false);
    }
  }, [draft, mode, saving]);

  const contextValue = useMemo(() => ({
    mode,
    editing: mode === 'editor',
    content,
    loaded,
    dirty,
    saving,
    valueFor,
    update,
    cancel,
    save,
  }), [mode, content, loaded, dirty, saving, valueFor, update, cancel, save]);

  return (
    <CmsContentContext.Provider value={contextValue}>
      {children}
    </CmsContentContext.Provider>
  );
};

export const useCmsContent = () => {
  const context = useContext(CmsContentContext);
  if (!context) throw new Error('useCmsContent must be used inside CmsContentProvider');
  return context;
};
